/**
 * `ConfigureDefaultEndpointModal` — ADR-0165 §Slice B.1 dashboard front.
 *
 * The merchant-facing surface for wiring a Track B (merchant HTTPS
 * endpoint) fulfillment lane onto ONE of the seven canonical DEFAULT
 * skills — `browse_catalog`, `check_availability`, `order_status`,
 * `track_shipment`, `recommend`. `place_order` and `request_refund`
 * are transactional (rail-gated) and stay first-party through
 * settlement — this modal does not offer them.
 *
 * Route reuse: the widened `POST /v1/skills` route (ADR-0165) accepts
 * a canonical default skill_id when `fulfillment_kind = 'webhook'`;
 * it UPDATEs the seeded default row + INSERTs the vault. The wire
 * body carries empty `name` / `description` — the server ignores
 * them for the default lane (the seeded values ARE the signed-card
 * contract; merchants add transport, not text).
 *
 * MCP server option REMOVED (2026-10-02): dispatch + the activation
 * battery only speak webhook on the default lane (ADR-0172 §4), so a
 * `fulfillment_kind: 'mcp_passthrough'` config saved against a default
 * skill id never routes — the honesty gate rejects it, the admission
 * gate rejects it, and the activation battery cannot probe it. The
 * modal shipped the option anyway, which left merchants with a config
 * that silently never worked. Webhook is now the only option here;
 * the server validator rejects `default + mcp_passthrough` with a
 * clear reason for defense in depth. Custom-skill `mcp_passthrough`
 * (ADR-0162 lane) is unaffected and lives in a separate modal.
 *
 * Shown-once secret: on success the server returns a fresh
 * `webhook_secret`; the merchant sees it exactly once via
 * `SecretShownOnce`. Rotate is available thereafter via the same
 * SettingsSkills row's rotate affordance (route also widened).
 *
 * The PII posture on Track B (ADR-0165 §"PII / customer-scoping — the
 * load-bearing shift for Track B") is documented in the body copy:
 * for `order_status` / `track_shipment`, the merchant's handler is
 * responsible for verifying that `customer_email` matches the order
 * before returning data. Sill validates the response SHAPE (no
 * `customer_address` / `customer_phone` — the canonical
 * `SkillResult.data` schema is `additionalProperties: false`) but
 * cannot check the email↔order match without knowing the merchant's
 * customer DB.
 *
 * Reuses `Modal`, `Field`, and `SecretShownOnce` atoms from the
 * existing custom-skill surface — same styling tokens, same shown-
 * once flow. Owner/admin-gated at the invocation site
 * (SettingsSkills.tsx); the server 403s otherwise.
 */

import { useEffect, useState } from 'react';

import type { Skill, SiteId } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useC } from '../../lib/theme.js';
import { Field, Modal } from '../../components/atoms/index.js';
import { SecretShownOnce } from './SecretShownOnce.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/** ADR-0165 §Slice B.1 — the read-side default skills the Track B lane
 *  serves. `place_order` + `request_refund` stay first-party (T2, rail-
 *  gated, money-movement) and are NOT offered here. */
type TrackBSkillId =
  | 'browse_catalog'
  | 'check_availability'
  | 'order_status'
  | 'track_shipment'
  | 'recommend';

export interface ConfigureDefaultEndpointModalProps {
  open: boolean;
  onClose: () => void;
  site_id: SiteId;
  /** The default skill row the merchant is attaching Track B to. */
  skill: Skill;
  onConfigured: (skill: Skill) => void;
}

function serverReasonCopy(err: ApiError): string {
  if (err.kind === 'rate_limited') {
    return 'Too many changes — try again in a moment.';
  }
  switch (err.server_reason) {
    case 'skill_exists':
      // Should not happen on the default lane (the row is pre-seeded),
      // but keep the mapping honest.
      return 'This skill is already configured.';
    case 'fulfillment_endpoint_invalid':
      return 'Endpoint must be HTTPS on your verified domain.';
    case 'fulfillment_kind_invalid':
      // The server rejects `default + mcp_passthrough` with this reason
      // (ADR-0172 §4 — MCP framing is not wired on the default lane).
      // The dashboard surfaces the honest status; the modal only sends
      // webhook, so this branch should only fire via a stale client.
      return 'Default skills accept webhook endpoints only.';
    case 'role_insufficient':
      return 'Only owners and admins can configure endpoints.';
    case 'webhook_fulfillment_unavailable':
      return "Endpoint fulfillment isn't available on this deployment.";
    case 'feature_disabled':
      // ADR-0165 §Slice B.1 — the Track B DEFAULT-skill lane is DORMANT
      // until Slice B.2 mitigations land (contract docs, red-team
      // battery). The server refuses with `feature_disabled` and the
      // dashboard surfaces the honest status. Custom-skill webhooks
      // (ADR-0162) are unaffected.
      return "Default-skill endpoints aren't available yet — coming soon.";
    default:
      break;
  }
  if (err.kind === 'bad_request') return 'Check the endpoint and try again.';
  if (err.kind === 'forbidden' || err.kind === 'not_found') {
    return 'You do not have permission to configure this skill.';
  }
  if (err.kind === 'service_unavailable') {
    return 'The service is temporarily unavailable. Try again in a moment.';
  }
  return 'Could not save the endpoint. Try again.';
}

/** Skills whose merchant handler receives `customer_email` and must
 *  verify it matches the order before returning data. The PII posture
 *  banner surfaces on these two only. */
const PII_SCOPED_SKILL_IDS: ReadonlySet<string> = new Set([
  'order_status',
  'track_shipment',
]);

export function ConfigureDefaultEndpointModal({
  open,
  onClose,
  site_id,
  skill,
  onConfigured,
}: ConfigureDefaultEndpointModalProps): JSX.Element | null {
  const C = useC();
  const api = useApi();

  const [endpoint, setEndpoint] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [minted, setMinted] = useState<
    { secret: string; skill: Skill } | null
  >(null);

  useEffect(() => {
    if (open) {
      setEndpoint('');
      setErrorMsg(null);
      setSubmitting(false);
      setMinted(null);
    }
  }, [open]);

  const trimmed = endpoint.trim();
  const valid = trimmed.length > 0 && /^https:\/\//i.test(trimmed);
  const canSubmit = valid && !submitting;
  const is_pii_scoped = PII_SCOPED_SKILL_IDS.has(String(skill.skill_id));

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      // The name + description fields are IGNORED by the widened
      // POST /v1/skills route for the default lane (ADR-0165 §Slice
      // B.1 — the seeded values are the signed-card contract). Pass
      // the current display strings so any future stricter validation
      // still sees consistent input.
      const res = await api.skills.createCustom(site_id, {
        skill_id: String(skill.skill_id) as TrackBSkillId,
        name: skill.name,
        description: skill.description,
        // The default-skill lane only accepts webhook today; MCP
        // passthrough is dead-lettered server-side (ADR-0172 §4) and
        // the picker has been removed to keep this honest.
        fulfillment_kind: 'webhook',
        fulfillment_endpoint: trimmed,
      });
      if (typeof res.webhook_secret === 'string') {
        setMinted({ secret: res.webhook_secret, skill: res.skill });
      } else {
        onConfigured(res.skill);
        onClose();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMsg(serverReasonCopy(err));
      } else {
        setErrorMsg('Could not save the endpoint. Try again.');
      }
      setSubmitting(false);
    }
  };

  if (open && minted !== null) {
    return (
      <Modal
        open={open}
        onClose={() => {
          onConfigured(minted.skill);
          onClose();
        }}
        size="md"
        title="Copy your endpoint secret"
        subtitle="Shown once. Sill never displays it again."
      >
        <div className="p-6 space-y-4">
          <SecretShownOnce secret={minted.secret} />
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                onConfigured(minted.skill);
                onClose();
              }}
              className="flex-1 sm:flex-none px-6 py-2.5"
              style={{
                background: C.lime,
                color: C.ink,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2,
              }}
            >
              I have copied the secret
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={`Configure endpoint · ${skill.name}`}
      subtitle="Route this skill to your own HTTPS endpoint."
      closeOnBackdrop={false}
    >
      <div className="p-6 space-y-4">
        <Field
          label="Fulfillment endpoint"
          hint="Must be an HTTPS URL on your verified domain. Sill will POST a signed request body to this URL; your handler verifies the HMAC and returns the canonical response shape."
          required
        >
          <input
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder={`https://hooks.your-site.com/skills/${String(skill.skill_id)}`}
            className="w-full px-3 py-2.5 outline-none"
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              color: C.text,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
            data-testid="configure-endpoint-url"
          />
        </Field>
        {is_pii_scoped && (
          <div
            className="p-3"
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
                marginBottom: 6,
              }}
            >
              CUSTOMER SCOPING — YOUR RESPONSIBILITY
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
              }}
            >
              Sill sends your handler <code>customer_email</code> alongside the
              order. YOUR handler must verify the email belongs to the order
              before returning data. Sill validates the response shape but
              cannot check the email match — only your system knows the
              customer.
            </div>
          </div>
        )}
        <div
          className="p-3"
          style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 1.5,
              marginBottom: 6,
            }}
          >
            WHAT HAPPENS ON SAVE
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.5,
            }}
          >
            Sill probes your endpoint, mints a shared HMAC secret (shown once),
            and republishes your signed skill manifest. The agent's call for{' '}
            <code>{String(skill.skill_id)}</code> is proxied to your endpoint
            with the canonical arguments; your response is validated against
            the canonical result shape before it reaches the agent.
          </div>
        </div>

        {errorMsg !== null ? (
          <div
            role="alert"
            data-testid="configure-endpoint-error"
            className="p-3"
            style={{
              background: C.coralDim,
              border: `1px solid rgba(255,107,71,0.3)`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              lineHeight: 1.5,
            }}
          >
            {errorMsg}
          </div>
        ) : null}
      </div>
      <div
        className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 px-6 py-4"
        style={{ borderTop: `1px solid ${C.border}` }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="px-4 py-2.5"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.muted,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            void submit();
          }}
          disabled={!canSubmit}
          data-testid="configure-endpoint-submit"
          className="flex-1 sm:flex-none px-6 py-2.5"
          style={{
            background: canSubmit ? C.lime : C.surface2,
            color: canSubmit ? C.ink : C.muted,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            opacity: canSubmit ? 1 : 0.6,
          }}
        >
          {submitting ? 'Saving…' : 'Save endpoint'}
        </button>
      </div>
    </Modal>
  );
}

